/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';

export interface DropdownPosition {
  top: number;
  left: number;
  right: number;
  width: number;
  openUpward: boolean;
  openLeftward: boolean;
}

// Estimated open-menu height: no real height exists to measure until after
// paint, so this is a conservative budget for deciding whether to flip
// upward (roughly a `max-h-60` listbox plus a search row).
const ESTIMATED_MENU_HEIGHT = 280;

/**
 * Derives where a dropdown anchored to `rect` should render so it never
 * bleeds past the viewport.
 *
 * @param rect - The trigger's current bounding rect.
 * @returns The position to render the dropdown at.
 */
function computePosition(rect: DOMRect): DropdownPosition {
  // A trigger pinned near the bottom of the viewport (a fixed bottom bar,
  // the last row of a long form) has no room for a menu opening downward --
  // its options render off-screen and are unreachable. Flip upward whenever
  // there's more room above than below and below alone can't fit the menu.
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openUpward = spaceBelow < ESTIMATED_MENU_HEIGHT && spaceAbove > spaceBelow;
  // A trigger that shrinks to fit a short selected value (or simply sits in
  // a narrow column) can be close enough to the right edge that growing the
  // menu rightward from the trigger's left edge leaves almost no room for a
  // longer option label -- mirror the vertical flip: grow from the
  // trigger's right edge instead, toward whichever side has more room.
  const openLeftward = rect.left > window.innerWidth / 2;
  return {
    // Viewport coords + position:fixed so the menu stacks above Dialog
    // (z-10000) and stays aligned when the shell body scrolls.
    top: openUpward ? rect.top : rect.bottom,
    left: rect.left,
    right: window.innerWidth - rect.right,
    width: rect.width,
    openUpward,
    openLeftward,
  };
}

/**
 * Tracks a trigger's bounding rect while a portalled dropdown is open and
 * derives where the dropdown should render so it never bleeds past the
 * viewport, re-measuring on every scroll (capture-phase, so a scroll on any
 * ancestor container counts) and on resize.
 *
 * Shared by every portalled dropdown trigger in the app -- the Select
 * component's own desktop menu, and any other trigger (a compact pill, a
 * cell-level picker) that needs the same viewport-aware positioning without
 * adopting Select's full-size form-field chrome.
 *
 * @param containerRef - Ref to the trigger element the dropdown is anchored to.
 * @param isOpen - Whether the dropdown is currently open.
 * @returns The computed position, or `null` while closed or unmeasured.
 */
export function useDropdownPosition(
  containerRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
): DropdownPosition | null {
  const [pos, setPos] = useState<DropdownPosition | null>(null);

  useEffect(() => {
    if (!isOpen || !containerRef.current) { setPos(null); return; }
    const update = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) setPos(computePosition(rect));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [isOpen, containerRef]);

  return pos;
}

/**
 * Builds the inline style for a portalled dropdown panel from a computed
 * {@link DropdownPosition}: anchored to whichever vertical/horizontal edge
 * has room, sized to fit its content without ever narrowing below the
 * trigger or bleeding past the viewport.
 *
 * @param pos - The position computed by {@link useDropdownPosition}.
 * @param zIndex - Stacking order for the panel.
 * @returns A `React.CSSProperties` object ready to spread onto the panel.
 */
export function dropdownPanelStyle(pos: DropdownPosition, zIndex: number): React.CSSProperties {
  return {
    position: 'fixed',
    ...(pos.openUpward
      ? { bottom: window.innerHeight - pos.top + 4 }
      : { top: pos.top + 4 }),
    ...(pos.openLeftward
      ? { right: pos.right, maxWidth: `calc(100vw - ${pos.right}px - 16px)` }
      : { left: pos.left, maxWidth: `calc(100vw - ${pos.left}px - 16px)` }),
    minWidth: pos.width,
    // No explicit `width` -- a fixed-position element with `left`/`right`
    // but no `width` set shrinks to fit its content by the browser's own
    // well-tested algorithm. An explicit `width: max-content` did the same
    // in isolation, but combined with `w-full` option rows inside a nested
    // `overflow-auto` listbox it rendered the whole menu stretched to its
    // `maxWidth` budget instead (nearly the full row) in a real browser --
    // not reproducible by inspecting layout numbers alone, only by looking
    // at the thing.
    zIndex,
    pointerEvents: 'auto',
  };
}
